import { v4 as uuidv4 } from 'uuid';
import { sendVfx, sendVfxDespawn } from '../net/combatEvents';
import { dungeonService } from './DungeonService';
import {
  ensureGridSheet,
  getGridSheetDescriptor,
} from './LazySpritesheetService';
import { vfxConfig, isVfxComposition } from '../data/vfx.config';
import { IVfxPreset } from '../types/animation.types';
import { ISpellAnimation } from '../types/animation.types';
import { DirectionEnum } from '../types/enums';

export type { ISpellAnimation };

class AnimationService {
  private static instance: AnimationService;
  private spellAnimations: Map<string, ISpellAnimation> = new Map();
  // Animations whose lazily-parsed grid sheet isn't in the store yet. Held here
  // (not ticked, not rendered) until the parse resolves, then promoted to
  // `spellAnimations` — so a short VFX no longer expires before its texture lands.
  private pendingAnimations: Map<string, ISpellAnimation> = new Map();
  public dungeonService!: typeof dungeonService;

  private constructor() {}

  public static getInstance(): AnimationService {
    if (!AnimationService.instance) {
      AnimationService.instance = new AnimationService();
    }
    return AnimationService.instance;
  }

  public createAnimation(
    spriteName: string,
    x: number,
    y: number,
    duration: number,
    options?: {
      id?: string;
      direction?: DirectionEnum;
      originId?: string;
      isLooping?: boolean;
      animationSpeed?: number | 'auto';
      onComplete?: () => void;
      width?: number;
      height?: number;
      /** Multiplier applied to the resolved width/height (default 1). */
      scale?: number;
      anchor?: [number, number];
      rotation?: number;
      parallaxDepth?: number;
    },
  ): string {
    const sheetReady = ensureGridSheet(spriteName);
    const preset = vfxConfig[spriteName];
    const gridDescriptor = getGridSheetDescriptor(spriteName);
    const id = options?.id ?? uuidv4();
    const animation: ISpellAnimation = {
      id,
      spriteName,
      x,
      y,
      direction: options?.direction,
      rotation: options?.rotation,
      originId: options?.originId,
      elapsedTime: 0,
      duration,
      isLooping: false,
      onComplete: options?.onComplete,
    };

    if (preset && isVfxComposition(preset)) {
      animation.layers = options?.direction
        ? preset.layers.map((l) =>
            l.direction !== undefined
              ? l
              : { ...l, direction: options.direction },
          )
        : preset.layers;
    } else {
      const single = (preset ?? {}) as IVfxPreset;
      animation.isLooping = options?.isLooping ?? single.loop ?? false;
      animation.animationSpeed =
        options?.animationSpeed ?? single.animationSpeed ?? 'auto';
      const scale = options?.scale ?? 1;
      const baseW = options?.width ?? single.width ?? gridDescriptor?.cell;
      const baseH = options?.height ?? single.height ?? gridDescriptor?.cell;
      animation.width = baseW !== undefined ? baseW * scale : undefined;
      animation.height = baseH !== undefined ? baseH * scale : undefined;
      animation.anchor = options?.anchor ?? single.anchor;
      animation.parallaxDepth = options?.parallaxDepth ?? single.parallaxDepth;
    }

    if (sheetReady) {
      // Defer until the grid sheet is parsed, else the animation expires before
      // its texture lands and never renders (prod first-cast VFX bug). Drop it
      // if it was cancelled meanwhile, or if the parse failed.
      this.pendingAnimations.set(id, animation);
      sheetReady.then(
        () => {
          if (this.pendingAnimations.delete(id)) {
            this.spellAnimations.set(id, animation);
          }
        },
        () => {
          this.pendingAnimations.delete(id);
        },
      );
    } else {
      this.spellAnimations.set(id, animation);
    }
    sendVfx({
      id,
      spriteName,
      x,
      y,
      duration,
      direction: options?.direction,
      isLooping: options?.isLooping,
      animationSpeed: options?.animationSpeed,
      width: options?.width,
      height: options?.height,
      scale: options?.scale,
      anchor: options?.anchor,
      rotation: options?.rotation,
      parallaxDepth: options?.parallaxDepth,
    });
    return id;
  }

  public getSpellAnimations(): ISpellAnimation[] {
    return Array.from(this.spellAnimations.values());
  }

  public getSpellAnimationsByOrigin(originId: string): ISpellAnimation[] {
    const animations: ISpellAnimation[] = [];
    this.spellAnimations.forEach((animation) => {
      if (animation.originId === originId) {
        animations.push(animation);
      }
    });
    return animations;
  }

  public removeSpellAnimation(id: string): void {
    const wasPending = this.pendingAnimations.delete(id);
    const wasActive = this.spellAnimations.delete(id);
    if (wasPending || wasActive) {
      // Tell clients to drop their mirrored copy now — a looping/long VFX removed
      // early would otherwise linger client-side until its duration. No-op off
      // the host (broadcast is host-guarded), so solo/clients don't re-emit.
      sendVfxDespawn(id);
    }
  }

  public removeSpellAnimationsByOrigin(originId: string): void {
    const toRemove: string[] = [];
    this.spellAnimations.forEach((animation, id) => {
      if (animation.originId === originId) {
        toRemove.push(id);
      }
    });
    this.pendingAnimations.forEach((animation, id) => {
      if (animation.originId === originId) {
        toRemove.push(id);
      }
    });
    toRemove.forEach((id) => {
      this.pendingAnimations.delete(id);
      this.spellAnimations.delete(id);
      sendVfxDespawn(id);
    });
  }

  public updateSpellAnimations(deltaTime: number): void {
    const completed: string[] = [];

    this.spellAnimations.forEach((animation) => {
      animation.elapsedTime += deltaTime;

      if (animation.elapsedTime >= animation.duration) {
        completed.push(animation.id);
      }
    });

    completed.forEach((id) => {
      const animation = this.spellAnimations.get(id);
      if (animation?.onComplete) {
        animation.onComplete();
      }
      this.spellAnimations.delete(id);
    });
  }
}

export const animationService = AnimationService.getInstance();
